import axios, { AxiosResponse } from "axios";
import { UserUrl } from "./AUrl";
import { LoginResponse, ApiResponse } from "./login";

/*
The scope of a chosen role: an institution role or a department role.
 */
export type RoleEntityType = "institution" | "department";

export type ChooseRoleType = (
    jwt: string,
    refreshTokenId: string,
    role: string,
    entityType: RoleEntityType,
    entityId: number,
) => Promise<ApiResponse<LoginResponse>>;

/*
Scope a multi-role session to a single chosen role.

A multi-role user's initial login returns a `roles` list and a JWT that is not
yet scoped to a role. This exchanges that unscoped session for a role-scoped one:
the response mirrors a login (fresh jwt + refresh token + a user_session carrying
the chosen role/entity), which the caller stores as the active session.

@jwt: The (unscoped) JWT returned by login.
@refreshTokenId: user_session.refresh_token_id from the login response.
@role: The role name being chosen (e.g. "ADMIN", "TEACHER").
@entityType: Whether the role is scoped to an institution or a department.
@entityId: The institution or department id the role belongs to.
 */
export async function chooseRole(
    jwt: string,
    refreshTokenId: string,
    role: string,
    entityType: RoleEntityType,
    entityId: number,
): Promise<ApiResponse<LoginResponse>> {
    const url = new UserUrl().choose_role;
    if (!jwt) {
        return { status: 401, error: "JWT has expired" };
    }

    let response: AxiosResponse<any>;
    try {
        response = await axios.post<LoginResponse | string>(
            url,
            {
                refresh_token_id: refreshTokenId,
                role,
                entity_type: entityType,
                entity_id: entityId,
            },
            {
                headers: {
                    "Content-Type": "application/json",
                    token: jwt,
                },
                validateStatus: () => true,
            },
        );
    } catch (error) {
        return {
            status: 500,
            error: "Network or other error occurred",
        };
    }

    if (response.status === 200) {
        return {
            status: response.status,
            data: response.data as LoginResponse,
        };
    }
    return {
        status: response.status,
        error: response.data as string,
    };
}
